import { useQuery, useQueries } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { DisplayInput } from "@/components/Atoms/DisplayField";
import { useFieldFeedbackMap } from "@/components/Atoms/FeedbackIndicator/use-field-feedback-map";
import {
	DisplayFieldFeedback,
	DisplayFieldState,
} from "@/components/Atoms/DisplayField/display-field.shared";
import {
	DisplaySelect,
	DisplaySelectBadgeVariant,
	DisplaySelectValueType,
} from "@/components/Atoms/DisplayField/DisplaySelect";
import {
	fieldLessThanLength,
	fieldRequired,
} from "@/components/form/validation/create-common-validators";
import { OrganizationStatusActionModal } from "@/features/organizations/organization-status-action-modal";
import {
	ORGANIZATION_STATUS_ACTIONS,
	organizationStatusActionName,
	organizationStatusActionsFromPermissions,
	organizationStatusApiValue,
	type OrganizationStatusAction,
} from "@/features/organizations/organization-status-actions";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth";
import {
	OrganizationStatusCode,
	OrganizationStatusColorMap,
	organizationQueryOptions,
} from "@/services/organization";
import {
	useUpdateOrganization,
	useUpdateOrganizationStatus,
} from "@/services/organization/organization.mutations";
import { formatId } from "@/utils/format-id";
import { showApiErrorToast, showSuccessToast } from "@/utils/toast.utils";
import { SearchSchema } from "../$organizationId";

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/organizations/$organizationId/details",
)({
	component: DetailsTabComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

type FeedbackField = "name";

function DetailsTabComponent() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/organizations/$organizationId/details",
	});
	const orgById = useQuery(
		organizationQueryOptions.detail(params.organizationId),
	);
	const updateOrganization = useUpdateOrganization();
	const updateStatus = useUpdateOrganizationStatus();
	const { feedbackByField, setFieldFeedback } =
		useFieldFeedbackMap<FeedbackField>();

	const [isEditingName, setIsEditingName] = useState(false);
	const [nameDraft, setNameDraft] = useState("");
	const [nameError, setNameError] = useState<string | undefined>();
	const [pendingStatusAction, setPendingStatusAction] =
		useState<OrganizationStatusAction | null>(null);
	const [retireName, setRetireName] = useState("");

	const nameEditAccess = useQuery(
		authQueryOptions.checkAction({
			name: "UpdateOrganizationInfo",
			entityType: "Organization",
			entityId: params.organizationId,
		}),
	);
	const canEditName = nameEditAccess.data === true;

	const statusActionChecks = useQueries({
		queries: ORGANIZATION_STATUS_ACTIONS.map((action) =>
			authQueryOptions.checkAction({
				name: organizationStatusActionName(action),
				entityType: "Organization",
				entityId: params.organizationId,
			}),
		),
	});
	const statusActions = organizationStatusActionsFromPermissions(
		Object.fromEntries(
			ORGANIZATION_STATUS_ACTIONS.map((action, index) => [
				action,
				statusActionChecks[index]?.data === true,
			]),
		),
	);

	const organization = orgById.data;
	const organizationName = organization?.name ?? "";
	const statusName = organization?.status?.name ?? "";
	const statusLabel =
		(statusName &&
			t(
				`admin.organizations.drawer.detailsTab.statuses.${statusName.toLowerCase()}`,
			)) ||
		statusName;
	const statusBadgeColor =
		OrganizationStatusColorMap[organization?.status?.code ?? -1] ?? "gray";
	const nameLabel = t("Organization.Name", { useFieldInfo: true });

	const cancelNameEdit = () => {
		setNameError(undefined);
		setFieldFeedback("name", DisplayFieldFeedback.Idle);
		setNameDraft("");
		setIsEditingName(false);
	};

	const commitName = async () => {
		if (!isEditingName) return;
		const next = nameDraft.trim();
		if (next === organizationName) {
			cancelNameEdit();
			return;
		}
		if (!next) {
			setNameError(fieldRequired(t, nameLabel));
			return;
		}
		if (next.length > 100) {
			setNameError(fieldLessThanLength(t, nameLabel, 100));
			return;
		}
		setIsEditingName(false);
		setFieldFeedback("name", DisplayFieldFeedback.Saving);
		try {
			await updateOrganization.mutateAsync({
				id: params.organizationId,
				data: { name: next },
			});
			setNameError(undefined);
			setNameDraft("");
			setFieldFeedback("name", DisplayFieldFeedback.Saved);
		} catch (error) {
			setFieldFeedback("name", DisplayFieldFeedback.Idle);
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	const closeStatusAction = () => {
		if (updateStatus.isPending) return;
		setPendingStatusAction(null);
		setRetireName("");
	};

	const confirmStatusAction = async () => {
		if (!pendingStatusAction) return;
		const action = pendingStatusAction;
		try {
			await updateStatus.mutateAsync({
				id: params.organizationId,
				data: { status: organizationStatusApiValue(action) },
			});
			showSuccessToast({
				message: t(
					`admin.organizations.drawer.statusActions.toast.${
						action === "activate"
							? "activated"
							: action === "suspend"
								? "suspended"
								: action === "archive"
									? "archived"
									: "retired"
					}`,
					{ name: organizationName },
				),
			});
			setPendingStatusAction(null);
			setRetireName("");
			if (action === "archive" || action === "retire") {
				navigate({
					to: "/admin/organizations",
					replace: true,
				});
			}
		} catch (error) {
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	return (
		<div className="flex w-full flex-col">
			<DisplayInput
				label={nameLabel}
				value={isEditingName ? nameDraft : organizationName || undefined}
				state={
					isEditingName
						? DisplayFieldState.Active
						: nameError
							? DisplayFieldState.Error
							: DisplayFieldState.Enabled
				}
				feedback={feedbackByField.name ?? DisplayFieldFeedback.Idle}
				hintText={nameError}
				showHintText={Boolean(nameError)}
				showEdit={canEditName}
				editValue={isEditingName ? nameDraft : undefined}
				onEdit={
					canEditName
						? () => {
								setNameError(undefined);
								setIsEditingName(true);
								setNameDraft(organizationName);
							}
						: undefined
				}
				onEditChange={(value) => {
					setNameDraft(value);
					setNameError(undefined);
				}}
				onEditCommit={() => void commitName()}
				onEditCancel={cancelNameEdit}
			/>
			<DisplaySelect
				label={t("Organization.Status", { useFieldInfo: true })}
				data-testid="organization-details-status-select"
				value={statusLabel || undefined}
				valueType={DisplaySelectValueType.Badge}
				badgeVariant={DisplaySelectBadgeVariant.Dot}
				showValueIcon={false}
				badgeColor={statusBadgeColor}
				showEdit={statusActions.length > 0}
				options={
					statusActions.length > 0
						? statusActions.map((action) => ({
								id: action,
								label: t(
									`admin.organizations.drawer.statusActions.${action}.action`,
								),
								testId: `organization-details-status-action-${action}`,
							}))
						: undefined
				}
				onSelectionChange={(key) => {
					setRetireName("");
					setPendingStatusAction(key as OrganizationStatusAction);
				}}
			/>
			<DisplayInput
				label={t("Organization.TotalUserCount", { useFieldInfo: true })}
				value={String(organization?.totalUserCount ?? 0)}
				showEdit={false}
			/>
			<DisplayInput
				label={t("Organization.OAuthProvider", { useFieldInfo: true })}
				value={organization?.oAuthProvider?.name ?? undefined}
				showEdit={false}
			/>
			<DisplayInput
				label={t(
					"admin.organizations.drawer.detailsTab.multiFactorAuthentication",
				)}
				value={
					organization?.mfaEnabled
						? t("admin.organizations.drawer.detailsTab.enabled")
						: t("admin.organizations.drawer.detailsTab.disabled")
				}
				showEdit={false}
			/>
			<DisplayInput
				label={t("Organization.Id", { useFieldInfo: true })}
				value={formatId(organization?.id) || undefined}
				copyable
				copyText={organization?.id}
				showEdit={false}
			/>
			<OrganizationStatusActionModal
				isOpen={pendingStatusAction != null}
				variant={pendingStatusAction ?? "suspend"}
				organizationName={organizationName}
				typedName={retireName}
				isActivatingFromArchived={
					organization?.status?.code === OrganizationStatusCode.Archived
				}
				isLoading={updateStatus.isPending}
				onTypedNameChange={setRetireName}
				onConfirm={confirmStatusAction}
				onCancel={closeStatusAction}
			/>
		</div>
	);
}
